import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  message, 
  Card, 
  Popconfirm,
  Select,
  Tag
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import api from '../../utils/axios';
import DepartmentSelect from '../../components/DepartmentSelect';
import initManager from '../../utils/initManager';

const { Option } = Select;
const { TextArea } = Input;

const PositionsSettings = () => {
  const [positions, setPositions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [form] = Form.useForm();

  // 获取岗位列表
  const fetchPositions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/positions');
      console.log('岗位数据:', response.data);
      // 确保数据是数组格式
      const positionsData = response.data.success ? response.data.data : [];
      setPositions(Array.isArray(positionsData) ? positionsData : []);
      
      // 缓存数据并标记为已初始化
      initManager.setCachedData('positions', positionsData);
      initManager.setInitialized('positions');
    } catch (error) {
      console.error('获取岗位列表失败:', error);
      message.error('获取岗位列表失败');
      setPositions([]); // 出错时设置为空数组
    } finally {
      setLoading(false);
    }
  };

  // 获取部门列表
  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      const departmentsData = response.data.success ? response.data.data : [];
      setDepartments(Array.isArray(departmentsData) ? departmentsData : []);
      
      // 缓存部门数据
      initManager.setCachedData('departments', departmentsData);
      initManager.setInitialized('departments');
    } catch (error) {
      // 已不使用该状态，静默处理
    }
  };

  useEffect(() => {
    // 检查是否已初始化，避免重复获取数据
    if (!initManager.isInitialized('positions')) {
      fetchPositions();
    } else {
      // 使用缓存的数据
      const cachedData = initManager.getCachedData('positions');
      if (cachedData) {
        setPositions(cachedData);
      } else {
        // 如果缓存为空，重新获取
        fetchPositions();
      }
    }
    
    // 部门数据也需要检查
    if (!initManager.isInitialized('departments')) {
      fetchDepartments();
    } else {
      const cachedDeptData = initManager.getCachedData('departments');
      if (cachedDeptData) {
        setDepartments(cachedDeptData);
      } else {
        fetchDepartments();
      }
    }
  }, []);

  // 添加岗位
  const handleAdd = () => {
    setEditingPosition(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 编辑岗位
  const handleEdit = (record) => {
    setEditingPosition(record);
    form.setFieldsValue({
      name: record.name,
      code: record.code,
      department_id: record.department_id,
      description: record.description,
      status: record.status,
      probation_days: record.probation_days
    });
    setModalVisible(true);
  };

  // 删除岗位
  const handleDelete = async (id) => {
    try {
      await api.delete(`/positions/${id}`);
      message.success('岗位删除成功');
      fetchPositions();
    } catch (error) {
      console.error('删除岗位失败:', error);
      message.error(error.response?.data?.error || '删除岗位失败');
    }
  };

  // 提交表单
  const handleSubmit = async (values) => {
    try {
      if (editingPosition) {
        await api.put(`/positions/${editingPosition.id}`, values);
        message.success('岗位更新成功');
      } else {
        await api.post('/positions', values);
        message.success('岗位创建成功');
      }
      setModalVisible(false);
      fetchPositions();
    } catch (error) {
      console.error('保存岗位失败:', error);
      message.error(error.response?.data?.error || '保存岗位失败');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '岗位名称',
      dataIndex: 'name',
      key: 'name',
      width: 150
    },
    {
      title: '岗位编码',
      dataIndex: 'code',
      key: 'code',
      width: 150
    },
    {
      title: '所属部门',
      dataIndex: 'department_name',
      key: 'department_name',
      width: 150,
      render: (text, record) => (
        <Tag color={record.department_color}>{text}</Tag>
      )
    },
    {
      title: '试用期(天)',
      dataIndex: 'probation_days',
      key: 'probation_days',
      width: 120
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '启用' : '禁用'}
        </Tag>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (text) => new Date(text).toLocaleString('zh-CN', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 150,
      align: 'right',
      render: (_, record) => (
        <Space size="small" style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个岗位吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card
        title="岗位管理"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchPositions}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增岗位
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={positions}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1000 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
          }}
        />
      </Card>

      {/* 岗位编辑模态框 */}
      <Modal
        title={editingPosition ? '编辑岗位' : '新增岗位'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="岗位名称"
            rules={[{ required: true, message: '请输入岗位名称' }]}
          >
            <Input placeholder="请输入岗位名称" />
          </Form.Item>

          <Form.Item
            name="code"
            label="岗位编码"
            rules={[{ required: true, message: '请输入岗位编码' }]}
          >
            <Input placeholder="请输入岗位编码，如：HR_MANAGER" />
          </Form.Item>

          <Form.Item
            name="department_id"
            label="所属部门"
            rules={[{ required: true, message: '请选择所属部门' }]}
          >
            <DepartmentSelect placeholder="请选择部门" />
          </Form.Item>

          <Form.Item
            name="probation_days"
            label="试用期(天)"
            rules={[{ required: true, message: '请输入试用期天数' }]}
          >
            <Input type="number" min={0} placeholder="例如 90" />
          </Form.Item>

          <Form.Item
            name="description"
            label="岗位描述"
          >
            <TextArea rows={3} placeholder="请输入岗位描述" />
          </Form.Item>

          <Form.Item
            name="status"
            label="状态"
            initialValue="active"
          >
            <Select>
              <Option value="active">启用</Option>
              <Option value="inactive">禁用</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingPosition ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PositionsSettings; 